import dynamic from 'next/dynamic';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { DEFAULT_CURRENCY_CODE } from 'shared/enums/SitePreferences';
import { getAppliedFilters } from 'shared/utils/gridEventTrackingUtils';
import { getPriceSliderRange } from 'search/utils/getPriceSliderRange';
import { getPriceStateFromUrl } from 'search/utils/getPriceState';
import { IConstructorFacetRange } from 'shared/types/ConstructorFacet';
import { LocalizedCurrency } from 'shared/components/LocalizedCurrency';
import {
  convertPriceAndRoundDown,
  convertPriceAndRoundUp,
} from 'search/utils/convertPriceAndRound';
import { SearchFiltersHoverAndSelectedStyle } from 'search/styles/SearchStyles';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useCurrency } from 'shared/hooks/useCurrency';
import { useEventContext } from 'tracking/hooks/useEventContext';
import { getConstructorCategoriesStrings } from 'search/enums/translations';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';

const Slider = dynamic(() => import('shared/components/Slider').then((mod) => mod.Slider));

interface ISearchFilterPrice {
  facet: IConstructorFacetRange;
  resultIds: Array<string>;
}

const SearchFilterPriceDesktop = ({ facet, resultIds }: ISearchFilterPrice) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const hasFilterApplied = Object.keys(facet.status).length === 2;
  const router = useRouter();
  const { asPath, query } = router;
  const pathnameWithDynamicValuesIncluded: string = asPath?.split('?')[0];
  const eventTrackingEventContext = useEventContext();
  const { selectedCurrencyCode } = useCurrency();
  const currencyRates = facet?.data?.currency_rates;
  const selectedCurrencyRate = currencyRates?.[selectedCurrencyCode];
  const currencyConversionRate =
    selectedCurrencyCode !== DEFAULT_CURRENCY_CODE && selectedCurrencyRate
      ? selectedCurrencyRate
      : 1;
  const isConsumerSearchEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  const range: [number, number] = [facet.min, facet.max];
  const initialStateFromUrl = getPriceStateFromUrl(router);
  const [values, setValues] = useState(initialStateFromUrl || range);
  const [update, setUpdate] = useState(initialStateFromUrl || range);
  const [isSliderActive, setIsSliderActive] = useState(!!query?.lowest_price_cents || false);

  const toggleFilter = () => {
    setIsFilterOpen((prevState) => !prevState);

    if (!isFilterOpen) {
      sendTrackingEvent('FILTER_EXPAND_TAP', {
        event_context: eventTrackingEventContext,
        filter_name: facet.name,
        ...(query?.query && { search_string: query.query.toString() }),
      });
    }
  };

  const handleUpdate = (updatedValues) => {
    setUpdate(updatedValues);
  };

  const handleSlideStart = () => {
    setIsSliderActive(true);
  };

  const handleSlideEnd = (updatedValues: [number, number]) => {
    setValues(updatedValues);
    setIsSliderActive(true);

    void router.push(
      {
        pathname: pathnameWithDynamicValuesIncluded,
        query: { ...query, lowest_price_cents: `${updatedValues[0]}-${updatedValues[1]}` },
      },
      null,
      { shallow: true, scroll: true },
    );
    sendTrackingEvent('FILTER_APPLY_TAP', {
      event_context: eventTrackingEventContext,
      filter_name: facet.name,
      filter_selection: `${updatedValues[0]}-${updatedValues[1]}`,
      result_id: resultIds[0],
      ...(getAppliedFilters(query) && { applied_filters: getAppliedFilters(query) }),
      ...(query?.query && { search_string: query.query.toString() }),
      service_name: isConsumerSearchEnabled ? 'consumer_search' : 'constructor',
    });
  };

  useEffect(() => {
    if (Object.keys(facet.status).length === 0) {
      setValues(range);
      setUpdate(range);
      setIsSliderActive(false);
    } else if (initialStateFromUrl?.length === 2) {
      setValues(initialStateFromUrl);
      setUpdate(initialStateFromUrl);
      setIsSliderActive(true);
    }
  }, [JSON.stringify(facet)]);

  return (
    <>
      <SearchFilterPriceDesktop.Title
        data-qa="price_filter"
        onClick={toggleFilter}
        isFilterOpen={isFilterOpen}
        hasFilterApplied={hasFilterApplied}
      >
        {getConstructorCategoriesStrings(facet?.display_name)}{' '}
        {isSliderActive && (
          <span data-qa="price_filter_selection">
            (
            <LocalizedCurrency
              price={convertPriceAndRoundDown(update[0], currencyConversionRate)}
            />{' '}
            -{' '}
            <LocalizedCurrency price={convertPriceAndRoundUp(update[1], currencyConversionRate)} />
            {update[1] === 1000000 ? '+' : ''})
          </span>
        )}
      </SearchFilterPriceDesktop.Title>
      {isFilterOpen && (
        <SearchFilterPriceDesktop.Collapsible>
          <Slider
            data-qa="price_filter_slider"
            range={getPriceSliderRange(range)}
            handleSlideEnd={handleSlideEnd}
            handleSlideStart={handleSlideStart}
            handleUpdate={handleUpdate}
            step={5000}
            values={values}
          />
        </SearchFilterPriceDesktop.Collapsible>
      )}
    </>
  );
};

SearchFilterPriceDesktop.Title = styled.button<{
  isFilterOpen: boolean;
  hasFilterApplied: boolean;
}>`
  display: block;
  text-align: right;
  width: 100%;
  text-transform: capitalize;
  cursor: pointer;
  padding: 0 0 4px;
  ${({ isFilterOpen }) => isFilterOpen && SearchFiltersHoverAndSelectedStyle};
  ${({ hasFilterApplied }) => hasFilterApplied && SearchFiltersHoverAndSelectedStyle};

  &:hover {
    ${SearchFiltersHoverAndSelectedStyle};
  }
`;

SearchFilterPriceDesktop.Collapsible = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 32px;
`;

export default SearchFilterPriceDesktop;
